import { useState, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { api, formatApiError, getUpgradeInfo } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { LockedFeature, UpgradeNotice } from "../plan/PlanUI";
import { CHAT } from "../../constants/testIds";
import { X, FileText, Image as ImageIcon, Trash2 } from "lucide-react";
import { MaterialDropzone, ProcessingCard, uploadMaterialFile } from "./MaterialUpload";

export function MaterialModal({ open, onClose, conversationId, ensureConversation }) {
  const { user } = useAuth();
  const locked = (user?.plano || "gratis") === "gratis";
  const [materials, setMaterials] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [err, setErr] = useState(null);
  const [upgrade, setUpgrade] = useState(null);

  const refresh = useCallback(async (cid = conversationId) => {
    if (!cid) return;
    const { data } = await api.get(`/materials?conversation_id=${cid}`);
    setMaterials(data);
  }, [conversationId]);

  useEffect(() => { if (open) { setErr(null); setUpgrade(null); refresh(); } }, [open, refresh]);

  if (!open) return null;

  const onFiles = async (fileList) => {
    const file = fileList?.[0];
    if (!file || processing?.stage === "reading") return;
    setErr(null); setUpgrade(null);
    setProcessing({ filename: file.name || "arquivo", stage: "reading" });
    try {
      const cid = conversationId || (ensureConversation ? await ensureConversation() : null);
      if (!cid) throw new Error("Abra uma conversa primeiro.");
      await uploadMaterialFile(file, cid);
      await refresh(cid);
      setProcessing((p) => ({ ...p, stage: "done" }));
      setTimeout(() => setProcessing((p) => (p?.stage === "done" ? null : p)), 3000);
    } catch (ex) {
      setProcessing(null);
      const up = getUpgradeInfo(ex);
      if (up) setUpgrade(up);
      else setErr(ex?.response ? formatApiError(ex) : (ex?.message || "Não consegui ler esse arquivo."));
    }
  };

  const del = async (id) => {
    await api.delete(`/materials/${id}`);
    refresh();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div data-testid={CHAT.materialModal} className="w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="font-bold tracking-tight">Material do professor</h2>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        {locked ? (
          <LockedFeature
            titulo="Upload de material"
            descricao="Suba slides em PDF ou foto do quadro/caderno (com OCR por IA) e o assistente usa como fonte de verdade nas explicações, quiz e flashcards."
            planoNecessario="medio"
          />
        ) : (
        <div className="p-5 space-y-3">
          <MaterialDropzone onFiles={onFiles} busy={processing?.stage === "reading"} />

          <AnimatePresence>
            {processing && (
              <ProcessingCard
                filename={processing.filename}
                stage={processing.stage}
                onDismiss={() => setProcessing(null)}
              />
            )}
          </AnimatePresence>

          {upgrade && <UpgradeNotice info={upgrade} onDismiss={() => setUpgrade(null)} />}
          {err && <p className="text-sm text-red-400">{err}</p>}

          {materials.length > 0 && (
            <div className="space-y-1.5 mt-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Materiais nesta conversa</p>
              {materials.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2 text-sm">
                  {m.kind === "image" ? <ImageIcon className="h-3.5 w-3.5 text-violet-400" /> : <FileText className="h-3.5 w-3.5 text-violet-400" />}
                  <span className="flex-1 truncate text-xs">{m.filename}</span>
                  <button onClick={() => del(m.id)} className="text-muted-foreground hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
